/**
 * EmptyStates Component Library
 *
 * Pre-built empty state components for common scenarios.
 * Provides user guidance when no data is available.
 */

import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { BouncyButton } from '@/components/AnimatedElements';
import { EmptyStateConfig } from '@/types/screen.types';
import { useThemeColors } from '@/styles';

/**
 * Default empty state component
 */
export const DefaultEmptyState: React.FC<{ config: EmptyStateConfig }> = ({
  config,
}) => {
  const colors = useThemeColors();

  return (
    <div
      className="flex items-center justify-center p-4"
      style={{ minHeight: 'calc(400px * var(--sizing-multiplier, 1))' }}
    >
      <Card className="max-w-md w-full shadow-lg">
        <CardContent className="p-8 text-center space-y-4">
          {/* Icon */}
          <div
            className="mx-auto w-16 h-16 rounded-full flex items-center justify-center"
            style={{ backgroundColor: colors.border.light }}
          >
            {config.icon ? (
              <div style={{ color: colors.text.tertiary }}>{config.icon}</div>
            ) : (
              <svg
                className="w-10 h-10"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{ color: colors.text.tertiary }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                />
              </svg>
            )}
          </div>

          {/* Title */}
          <h3
            className="text-xl font-semibold"
            style={{ color: colors.text.primary }}
          >
            {config.title}
          </h3>

          {/* Description */}
          <p style={{ color: colors.text.secondary }}>{config.description}</p>

          {/* Action button */}
          {config.actionText && config.onAction && (
            <div className="pt-2">
              <BouncyButton
                onClick={config.onAction}
                className="py-2 px-6 rounded-lg shadow-md transition-colors"
                style={{
                  backgroundColor: colors.primary,
                  color: colors.text.inverse,
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor =
                    colors.hover.background;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = colors.primary;
                }}
              >
                {config.actionText}
              </BouncyButton>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * No data empty state - generic "no data" message
 */
export const NoDataEmpty: React.FC<{ onAction?: () => void }> = ({
  onAction,
}) => (
  <DefaultEmptyState
    config={{
      title: 'No Data Available',
      description: 'There is no data to display at the moment.',
      icon: (
        <svg
          className="w-10 h-10 "
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
      actionText: onAction ? 'Refresh' : undefined,
      onAction,
    }}
  />
);

/**
 * No results empty state - search/filter yielded no results
 */
export const NoResultsEmpty: React.FC<{ onClear?: () => void }> = ({
  onClear,
}) => (
  <DefaultEmptyState
    config={{
      title: 'No Results Found',
      description:
        "We couldn't find any items matching your search. Try adjusting your filters or search terms.",
      icon: (
        <svg
          className="w-10 h-10 "
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      ),
      actionText: onClear ? 'Clear Filters' : undefined,
      onAction: onClear,
    }}
  />
);

/**
 * No items empty state - collection is empty
 */
export const NoItemsEmpty: React.FC<{
  itemName: string;
  onCreate?: () => void;
}> = ({ itemName, onCreate }) => (
  <DefaultEmptyState
    config={{
      title: `No ${itemName} Yet`,
      description: `You haven't created any ${itemName.toLowerCase()} yet. Get started by creating your first one.`,
      icon: (
        <svg
          className="w-10 h-10 "
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
          />
        </svg>
      ),
      actionText: onCreate ? `Create ${itemName}` : undefined,
      onAction: onCreate,
    }}
  />
);

/**
 * Coming soon empty state - feature not ready yet
 */
export const ComingSoonEmpty: React.FC<{ featureName?: string }> = ({
  featureName = 'This feature',
}) => (
  <DefaultEmptyState
    config={{
      title: 'Coming Soon',
      description: `${featureName} is currently under development. Stay tuned for updates!`,
      icon: (
        <svg
          className="w-10 h-10 "
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    }}
  />
);
